import { differenceInDays, isAfter, subDays } from 'date-fns';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getBestSellersAnalytics,
  getPrevDayAnalytics,
  getSalesByDayAnalytics,
  getDashboardAdditionalData,
} from '../../../store/actions/analytics';
import { ANALYTICS_LOADING, ANALYTICS_SET_ADDITIONAL_DATA } from '../../../store/reducers/analytics';
import { ANALYTICS_START_DATE, REPORT_TYPES } from '../../tables/reports/consts';

import { DashboardHeader } from './DashboardHeader';
import { AnalyticsGraphs } from './AnalyticsGraphs';
import { AnalyticsMetrics } from './AnalyticsMetrics';
import { AnalyticsFooter } from './AnalyticsFooter';
import { SidebarPageLayout } from '../../layouts/SidebarPageLayout';
import { useAnalyticsMenuItems } from '../../../hooks/useAnalyticsMenuItems';

export function DashboardPage() {
  const dispatch = useDispatch();
  const {
    filters,
    ui: { loading },
  } = useSelector((state) => state.analytics);
  const { sidebarMenuItems } = useAnalyticsMenuItems();

  useEffect(() => {
    if (!loading.canLoadData) return;

    getSalesByDay();
    getPrevSales(filters.dates);
    getBestSellers();

    // eslint-disable-next-line
  }, [loading.canLoadData, filters.countries]);

  useEffect(() => {
    dispatch(getDashboardAdditionalData());
    // eslint-disable-next-line
  }, [filters.dates, filters.retailerIds]);

  const getSalesByDay = useCallback(() => {
    dispatch(
      getSalesByDayAnalytics((data) => {
        dispatch({
          type: ANALYTICS_SET_ADDITIONAL_DATA,
          payload: { kpi: REPORT_TYPES.SALES_BY_DAY, data: { [REPORT_TYPES.SALES_BY_DAY]: data } },
        });
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPrevSales = useCallback((dates) => {
    const { start: startDate, end: endDate } = dates;
    const days = differenceInDays(endDate, startDate) + 1;
    const shouldGetPrevData = isAfter(subDays(startDate, days), subDays(ANALYTICS_START_DATE, 1));
    if (!shouldGetPrevData) {
      dispatch({ type: ANALYTICS_SET_ADDITIONAL_DATA, payload: { kpi: 'prevData', data: { prevData: [] } } });
      dispatch({ type: ANALYTICS_LOADING, payload: { prevData: false } });
      return;
    }

    dispatch(
      getPrevDayAnalytics({
        startDate: subDays(startDate, days),
        endDate: subDays(endDate, days),
        onResponse: (data) => {
          dispatch({
            type: ANALYTICS_SET_ADDITIONAL_DATA,
            payload: { kpi: 'prevData', data: { prevData: data } },
          });
          dispatch({ type: ANALYTICS_LOADING, payload: { prevData: false } });
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBestSellers = useCallback(() => {
    dispatch(
      getBestSellersAnalytics((data) => {
        dispatch({
          type: ANALYTICS_SET_ADDITIONAL_DATA,
          payload: {
            kpi: REPORT_TYPES.BEST_SELLERS,
            data: { [REPORT_TYPES.BEST_SELLERS]: { data } },
          },
        });
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      dispatch({ type: ANALYTICS_LOADING, payload: { canLoadData: false } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SidebarPageLayout items={sidebarMenuItems} hasStickyHeader>
      <DashboardHeader title="Sales dashboard" />

      <main>
        <AnalyticsMetrics />
        <AnalyticsGraphs />
        <AnalyticsFooter />
      </main>
    </SidebarPageLayout>
  );
}
